import React, { useCallback, useEffect, useRef } from 'react';

import type { EChartsOption } from 'echarts';
import { BarChart, LineChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { init, use as registerECharts } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

import { formatRmTooltipValue } from './tooltipFormat';

registerECharts([BarChart, LineChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

export type RmSeries = {
  name: string;
  data: Array<number | null>;
  kind: 'line' | 'bar';
  axis?: 'left' | 'right';
  percent?: boolean;
};

export default function RmMonitorChart({
  title,
  categories,
  series,
  source,
  onSelect,
}: {
  title: string;
  categories: Array<string>;
  series: Array<RmSeries>;
  source: string;
  onSelect?: (category: string) => void;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const element = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<typeof init> | undefined>(undefined);
  const pending = useRef<EChartsOption | undefined>(undefined);
  const select = useRef(onSelect);
  select.current = onSelect;
  const labels = categories.map(dayLabel);
  const option: EChartsOption = {
    textStyle: { fontFamily: FONT_STACK_SUISSE_INTL, color: theme.palette.text.primary },
    color: ['#1f4b99', '#0f766e', '#b45309'],
    tooltip: {
      trigger: 'axis',
      confine: true,
      renderMode: 'richText',
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
      textStyle: { fontFamily: FONT_STACK_SUISSE_INTL, color: theme.palette.text.primary },
    },
    legend: { top: 0, textStyle: { fontFamily: FONT_STACK_SUISSE_INTL, color: theme.palette.text.secondary } },
    grid: { left: 64, right: series.some((entry) => entry.axis === 'right') ? 64 : 24, top: 36, bottom: 32 },
    xAxis: {
      type: 'category',
      data: categories,
      axisLine: { lineStyle: { color: tokens.axis } },
      axisLabel: {
        fontFamily: FONT_STACK_SUISSE_INTL,
        color: theme.palette.text.secondary,
        formatter: (_value, index) => labels[index] ?? '',
      },
    },
    yAxis: [
      {
        type: 'value',
        splitLine: { lineStyle: { color: tokens.grid } },
        axisLabel: {
          fontFamily: FONT_STACK_SUISSE_INTL,
          color: theme.palette.text.secondary,
          formatter: (value: number) =>
            series.find((entry) => entry.axis !== 'right')?.percent ? percent(value) : compact(value),
        },
      },
      ...(series.some((entry) => entry.axis === 'right')
        ? [
            {
              type: 'value' as const,
              axisLabel: {
                fontFamily: FONT_STACK_SUISSE_INTL,
                color: theme.palette.text.secondary,
                formatter: (value: number) =>
                  series.find((entry) => entry.axis === 'right')?.percent ? percent(value) : compact(value),
              },
              splitLine: { show: false },
            },
          ]
        : []),
    ],
    series: series.map((entry) => ({
      name: entry.name,
      type: entry.kind,
      yAxisIndex: entry.axis === 'right' ? 1 : 0,
      data: entry.data,
      showSymbol: entry.kind === 'line',
      tooltip: { valueFormatter: (value) => formatRmTooltipValue(value, entry.percent) },
    })),
  };
  const sync = useCallback(() => {
    const node = element.current;
    const chart = instance.current;
    if (!node?.clientWidth || !node.clientHeight || !chart || chart.isDisposed()) return;
    chart.resize();
    if (pending.current) {
      chart.setOption(pending.current, { notMerge: true });
      pending.current = undefined;
    }
  }, []);
  useEffect(() => {
    if (!element.current) return undefined;
    const chart = init(element.current, undefined, { renderer: 'canvas' });
    instance.current = chart;
    chart.on('click', (event: { name?: string }) => {
      if (event.name) select.current?.(event.name);
    });
    const observer = new ResizeObserver(sync);
    observer.observe(element.current);
    return () => {
      observer.disconnect();
      chart.dispose();
      instance.current = undefined;
    };
  }, [sync]);
  useEffect(() => {
    pending.current = option;
    sync();
  });
  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Typography
        component="h2"
        variant="h6"
        sx={{ px: 2, py: 1.5, bgcolor: tokens.plateHover, fontFamily: FONT_STACK_SUISSE_INTL }}
      >
        {title}
      </Typography>
      <Box
        ref={element}
        role="img"
        aria-label={onSelect ? `${title}. Select a point to filter.` : title}
        sx={{ height: 280 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', px: 2, pb: 1.5, fontFamily: FONT_STACK_SUISSE_INTL }}
      >
        {source}
      </Typography>
    </Paper>
  );
}

export function dayLabel(iso: string) {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return day && month && year ? `${day}/${month}/${year}` : iso;
}

function compact(value: number) {
  return new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

function percent(value: number) {
  return new Intl.NumberFormat('en-AU', { style: 'percent', maximumFractionDigits: 1 }).format(value);
}
